import SlotText from '@/components/feature/SlotText';
import RotatingBadge from '@/components/feature/RotatingBadge';
import { marqueeWords } from '@/mocks/portfolio';

export default function Hero() {
  return (
    <section id="top" className="relative bg-white md:bg-transparent pt-32 md:pt-44 pb-12 md:pb-16 overflow-hidden">
      {/* Ambient glow — subtle depth behind hero content */}
      <div className="absolute inset-0 pointer-events-none hidden md:block" aria-hidden="true">
        <div className="absolute top-1/3 -left-48 w-[38rem] h-[38rem] rounded-full bg-accent-500/5 blur-[140px]" />
      </div>
      <div className="relative mx-auto max-w-[1280px] px-6 md:px-10">

        {/* Label row */}
        <div className="flex items-center gap-2 mb-10 fade-up">
          <span className="w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0" />
          <span className="font-label text-[10px] tracking-[0.14em] uppercase text-foreground-500">
            Career Portfolio — Keiji Nakatsu
          </span>
        </div>

        {/* Name */}
        <h1 className="font-heading fw-bold text-[clamp(56px,10vw,136px)] leading-[0.85] tracking-[-0.05em] text-foreground-950 fade-up">
          KEIJI
          <br />
          NAKATSU
        </h1>

        {/* Title block */}
        <div className="mt-8 md:mt-12 fade-up">
          <p className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-500 mb-2">
            Primary Position
          </p>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <div className="flex-1 min-w-0">
              <p className="font-heading fw-bold text-[clamp(32px,5vw,68px)] leading-[1.0] tracking-[-0.02em] text-foreground-950">
                CX &amp; COMMUNICATION
                <br />
                <SlotText text="STRATEGIST" as="span" className="not-italic marker-yellow" scrambleColor="rainbow" />
              </p>
              <p className="mt-3 font-label fw-light text-[11px] tracking-[0.1em] uppercase text-foreground-600">
                Creative Director / Communication Planner
              </p>
            </div>
            <div className="group w-full h-56 md:w-64 md:h-64 lg:w-80 lg:h-80 flex-shrink-0 -mb-4 md:-mb-6 lg:-mb-8 relative self-end">
              <div className="w-full h-full animate-float bg-white md:bg-background-50 rounded-none">
                <img
                  src="/assets/migrated/12f69c9c4b3794c0570f.jpg"
                  alt="レオ（マルプーの男の子）とライア（ヨーキーの女の子）の可愛い線画イラスト"
                  title="レオちゃんとライアちゃんの線画イラスト — マルプーとヨーキーの可愛いモノクロ線画"
                  className="w-full h-full object-contain object-right wiggle-on-hover cursor-pointer"
                  style={{ mixBlendMode: 'multiply' }}
                />
              </div>
              <RotatingBadge
                text="CX • BRAND • DESIGN • STRATEGY • "
                size={104}
                className="absolute right-4 -top-16 md:right-auto md:-left-5 md:-top-14"
              />
            </div>
          </div>
        </div>

        {/* Main Statement & Supporting Statement */}
        <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 fade-up">
          <div className="md:col-span-7">
            <div className="border-t-2 border-foreground-950 pt-6">
              <p className="font-heading fw-bold text-[clamp(32px,5vw,68px)] text-foreground-950 leading-[0.93] tracking-[-0.03em]">
                BUSINESS ISSUE
                <br className="md:hidden" />
                <span className="hidden md:inline">
                  <br />
                </span>
                TO <SlotText text="EXPERIENCE" as="span" className="fw-medium marker-yellow" scrambleColor="rainbow" />
                <span className="md:hidden"> &amp;</span>
                <br />
                <span className="hidden md:inline">&amp; </span>COMMUNICATION.
              </p>
            </div>
          </div>
          <div className="md:col-span-4 md:col-start-9 flex items-end">
            <div>
              <p className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-500 mb-3">
                Supporting Statement
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                事業・市場・顧客課題を理解し、価値を定義し、CX・ブランド・コミュニケーションを設計する。Web・映像・空間・リテール・サービスなど具体的な顧客接点まで、一貫して実装する。
              </p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-14 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 fade-up">
          {[
            { k: '20+', v: 'Years Experience', sub: '空間・リテール・広告・CXを横断' },
            { k: '100+', v: 'Projects', sub: '多様な領域・産業での実績' },
            { k: '5', v: 'Companies', sub: '組織を超えた課題解決経験' },
            { k: '6', v: 'Step Approach', sub: '分析から実装まで一貫設計' },
          ].map((s) => (
            <div key={s.v} className="border-t border-foreground-950 pt-4">
              <SlotText
                text={s.k}
                as="p"
                className="font-heading text-[clamp(28px,4vw,52px)] text-foreground-950 leading-none tracking-[-0.04em]"
              />
              <p className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-500 mt-2">
                {s.v}
              </p>
              <p className="text-xs text-foreground-400 mt-1 leading-tight hidden md:block">
                {s.sub}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll cue */}
      <div className="mt-14 md:mt-20 flex justify-center fade-up">
        <div className="flex flex-col items-center gap-3">
          <span className="font-label text-[9px] tracking-[0.14em] uppercase text-foreground-400">Scroll</span>
          <div className="relative w-px h-10 bg-foreground-200 overflow-hidden">
            <span className="absolute left-0 top-0 w-full h-2 bg-primary-500 animate-scroll-dot" />
          </div>
        </div>
      </div>

      {/* Marquee */}
      <div className="mt-16 md:mt-24 border-0 md:border-y border-background-300 py-6 md:py-5 overflow-hidden relative">
        {/* Mobile: colorful rainbow gradient lines top & bottom */}
        <div className="absolute top-0 left-0 right-0 h-[2px] overflow-hidden md:hidden">
          <div
            className="w-full h-full"
            style={{
              backgroundImage: `linear-gradient(
                90deg,
                oklch(0.82 0.17 20 / 0) 0%,
                oklch(0.84 0.16 55 / 0.85) 14%,
                oklch(0.80 0.15 115 / 0.95) 36%,
                oklch(0.76 0.14 175 / 0.95) 58%,
                oklch(0.73 0.16 240 / 0.88) 80%,
                oklch(0.76 0.19 305 / 0.68) 95%,
                oklch(0.76 0.19 305 / 0) 100%
              )`,
            }}
          />
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-[2px] overflow-hidden md:hidden">
          <div
            className="w-full h-full"
            style={{
              backgroundImage: `linear-gradient(
                90deg,
                oklch(0.76 0.19 305 / 0) 0%,
                oklch(0.76 0.19 305 / 0.68) 5%,
                oklch(0.73 0.16 240 / 0.88) 20%,
                oklch(0.76 0.14 175 / 0.95) 42%,
                oklch(0.80 0.15 115 / 0.95) 64%,
                oklch(0.84 0.16 55 / 0.85) 86%,
                oklch(0.82 0.17 20 / 0) 100%
              )`,
            }}
          />
        </div>
        {/* Mobile fade edges */}
        <div className="absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none md:hidden" />
        <div className="absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none md:hidden" />
        <div className="flex animate-marquee whitespace-nowrap">
          {[...marqueeWords, ...marqueeWords].map((w, i) => (
            <span
              key={i}
              className="font-heading text-xl md:text-3xl text-foreground-950 mx-6 flex items-center tracking-[-0.02em]"
            >
              {w}
              <span className="ml-6 w-1.5 h-1.5 rounded-full bg-primary-500 inline-flex items-center justify-center" />
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}